import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import {
  CLOSE_ICON_WHITE,
  EDIT_ICON_WHITE,
  EMPTY_PROFILE_PIC,
  HEADING_TEXT_WHITE_1,
  HEADING_TEXT_WHITE_2,
  REQUESTED_STATUS_CARD,
} from "../../utilities/generatedClasses";
import { classNameTriskeleParser } from "../../utilities/customStyles";
import Icon from "../../components/Icon";
import ProfileAccess from "../../dataAccess/ProfileAccess";
import JotformAccess from "../../dataAccess/JotformAccess";
import { groupBy } from "../../utilities/utils";
import UserProfileSkeleton from "./skeleton/UserProfileSkeleton";
import { useNavigate, useSearchParams } from "react-router-dom";

// Panel mode: pass `close` + panelClientId or panelProviderId
//   Opens immediately with skeleton, fetches its own data
// Page mode:  no `close` prop, routed component
//   Fetches based on ?providerId / ?clientId URL params or location state userId

function ProfileView(props) {
  const { close, isUserProfile, panelClientId, panelProviderId, panelClientContext } = props;
  const isPanelMode = Boolean(close);

  const [profileHeader, setProfileHeader] = React.useState(null);
  const [profileFields, setProfileFields] = React.useState([]);
  const [allFields, setAllFields] = React.useState([]);
  const [userType, setUserType] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(true);
  const [isNavigating, setIsNavigating] = React.useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userId } = props.location?.state ?? {};
  const providerId = searchParams.get("providerId");
  const clientId = searchParams.get("clientId");

  React.useEffect(() => {
    if (isPanelMode) {
      if (panelClientId) {
        setUserType("client");
        ProfileAccess.getClientProfile(panelClientId)
          .then(processResponse)
          .finally(() => setIsLoading(false));
      } else if (panelProviderId) {
        setUserType("provider");
        ProfileAccess.getProviderProfile(panelProviderId, panelClientContext)
          .then(processResponse)
          .finally(() => setIsLoading(false));
      }
      return;
    }
    if (providerId) {
      setUserType("provider");
      ProfileAccess.getProviderProfile(providerId, clientId)
        .then(processResponse)
        .finally(() => setIsLoading(false));
    } else if (userId) {
      setUserType("client");
      ProfileAccess.getClientProfile(userId)
        .then(processResponse)
        .finally(() => setIsLoading(false));
    } else {
      setUserType("user");
      ProfileAccess.getUserProfile()
        .then(processResponse)
        .finally(() => setIsLoading(false));
    }
  }, []);

  const processResponse = (response) => {
    const data = response.data;
    setProfileHeader(data.find((el) => el.PrcsParmtrValueOne === "Header") ?? null);
    const sections = data.filter((el) => el.PrcsParmtrValueOne === "Field");
    const grouped = groupBy(sections, (s) => s.ProfileCtgy);
    setProfileFields(Array.from(grouped, ([name, value]) => ({ name, value })));
    setAllFields(data);
  };

  const openEditProfile = () => {
    setIsNavigating(true);
    JotformAccess.getJotformToken()
      .then((response) => {
        if (!response?.data) return;
        const userToken = response.data;
        const getField = (name) =>
          allFields.find((x) => x.FieldName === name)?.FieldValue || "";

        const dateOfBirth = getField("Date of Birth");
        const orgLocation = getField("Location");
        const cleaned = orgLocation.replace(/\\n/g, "\n");
        const locationParts = cleaned.split("\n").filter(Boolean);
        const [street, floorOrCity, maybeCity] = locationParts;
        const streetLine = street || null;
        const floor = locationParts.length === 3 ? floorOrCity : null;
        const cityStateZip = locationParts.length === 3 ? maybeCity : floorOrCity;

        let city = null, state = null, postal = null;
        if (cityStateZip) {
          const match = cityStateZip.trim().match(/^(.+?),\s*([A-Za-z\s]+)\s+(\d{5})$/);
          if (match) {
            city = match[1]?.trim();
            state = match[2]?.trim();
            postal = match[3]?.trim();
          } else {
            postal = cityStateZip.trim();
          }
        }

        const qualificationLocation = getField("Qualificaiton Location");
        let qualificationState = null, qualificationCountry = null;
        if (qualificationLocation) {
          const qParts = qualificationLocation.split(",");
          qualificationState = qParts[0]?.trim() || null;
          qualificationCountry = qParts[1]?.trim() || null;
        }

        const rawParams = {
          userToken,
          firstName: getField("First Name"),
          preferredName: getField("Preferred Name"),
          middleName: getField("Middle Name"),
          lastName: getField("Last Name"),
          "phoneNumber[full]": getField("Phone Number"),
          emailAddress: getField("Email Address"),
          postnominalLetters: getField("Post-Nominal Letters"),
          sexAssigned: getField("Sex Assigned at Birth"),
          userPronouns: getField("Pronouns"),
          prenominalLetters: getField("Pre-Nominal Letters"),
          webAddress: getField("Web Address"),
          qualificationType: getField("Qualificaiton Type"),
          qualificationValue: getField("Qualificaiton Number"),
          emailType: getField("Email Type"),
          phoneType: getField("Phone Type"),
          qualificationState,
          qualificationCountry,
          professionalTitle: getField("Professional Title"),
          speciality: getField("Specialty"),
          organization: getField("Organization"),
          population: getField("Population"),
          "organizationAddress[addr_line1]": streetLine,
          "organizationAddress[addr_line2]": floor,
          "organizationAddress[city]": city,
          "organizationAddress[state]": state,
          "organizationAddress[postal]": postal,
          "dateofBirth[day]": dateOfBirth ? dateOfBirth.split("-")[1] : "",
          "dateofBirth[month]": dateOfBirth ? dateOfBirth.split("-")[0] : "",
          "dateofBirth[year]": dateOfBirth ? dateOfBirth.split("-")[2] : "",
          formName: "Prism_Provider_Profile",
        };

        const filtered = Object.fromEntries(
          Object.entries(rawParams).filter(
            ([, v]) => v !== null && v !== undefined && v !== "" && v !== "unknown"
          )
        );

        navigate("/home/editprofile", {
          state: { profileUrl: `${window.jotformUrl}?${new URLSearchParams(filtered).toString()}` },
        });
      })
      .catch(() => setIsNavigating(false));
  };

  const handleClose = isPanelMode ? close : () => navigate(-1);
  const showEditButton = isPanelMode ? isUserProfile : userType === "user";

  const getHeaderName = () => {
    if (!profileHeader) return null;
    if (userType === "user") return profileHeader.UserHeader;
    if (userType === "client") return profileHeader.ClientHeader;
    return profileHeader.ProviderHeader;
  };

  const cardClass = `clinician-profile-card ${classNameTriskeleParser("Background", "Faded Navy", "Rectangle")}`;

  if (isLoading) {
    return (
      <div
        className={isPanelMode ? "client-profile-container" : undefined}
        style={!isPanelMode ? { position: "relative" } : undefined}
      >
        <UserProfileSkeleton />
      </div>
    );
  }

  return (
    <div
      className={isPanelMode ? "client-profile-container" : undefined}
      style={!isPanelMode ? { position: "relative" } : undefined}
    >
      {!isPanelMode && isNavigating && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.6)",
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </div>
      )}

      <div className={cardClass}>
        <div
          style={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}
        >
          <div style={{ margin: "10px", position: "relative" }}>
            {profileHeader?.ProfilePicture ? (
              <img
                className="profile-header-main-user-image x-large_size"
                src={profileHeader.ProfilePicture}
                alt="user profile"
              />
            ) : (
              <Icon classNames={`x-large_size ${EMPTY_PROFILE_PIC}`} />
            )}
            {showEditButton && (
              <div
                className="edit-container"
                style={!isPanelMode ? { right: "20px" } : undefined}
                aria-hidden
                onClick={!isPanelMode ? openEditProfile : undefined}
              >
                <div className={`${EDIT_ICON_WHITE} edit-icon`} />
              </div>
            )}
          </div>
          <div
            style={{
              position: "absolute",
              top: isPanelMode ? "0" : "10px",
              right: isPanelMode ? "25px" : "15px",
            }}
            onClick={handleClose}
            aria-hidden="true"
          >
            <Icon classNames={`large_size ${CLOSE_ICON_WHITE}`} />
          </div>
        </div>

        {profileHeader ? (
          <>
            <label
              className={`${!isPanelMode ? "mb-0 " : ""}${classNameTriskeleParser(
                profileHeader.VisualNameOne,
                profileHeader.VisualTypeOne,
                "Text"
              )}`}
            >
              {getHeaderName()}
              {!isPanelMode && (
                <span
                  className={classNameTriskeleParser(
                    profileHeader.VisualNameTwo,
                    profileHeader.VisualTypeTwo,
                    "Text"
                  )}
                >
                  {" "}
                  {profileHeader.PostNominalLetters}
                </span>
              )}
            </label>

            {isPanelMode && userType === "provider" && (
              <label
                className={`mt-10 ${classNameTriskeleParser(
                  profileHeader.VisualNameTwo,
                  profileHeader.VisualTypeTwo,
                  "Text"
                )}`}
              >
                {profileHeader.PostNominalLetters}
              </label>
            )}

            {userType === "provider" && (
              <label
                className={`${isPanelMode ? "mt-10" : "mb-0"} ${classNameTriskeleParser(
                  profileHeader.VisualNameThree,
                  profileHeader.VisualTypeThree,
                  "Text"
                )}`}
              >
                {profileHeader.ProviderSubHeader}
              </label>
            )}

            {isPanelMode && userType === "client" && profileHeader.Status && (
              <div
                style={{ alignSelf: "flex-end", marginRight: "25px", marginTop: "100px" }}
                className={classNameTriskeleParser(
                  profileHeader.VisualNameFour,
                  profileHeader.VisualTypeFour,
                  "Card"
                )}
              >
                <p style={{ margin: 0 }}>{profileHeader.Status}</p>
              </div>
            )}
          </>
        ) : (
          isPanelMode && (
            <>
              <label className={`mb-0 ${HEADING_TEXT_WHITE_1}`} style={{ opacity: 0 }}>
                None
              </label>
              <label className={`mb-0 ${HEADING_TEXT_WHITE_2}`} style={{ opacity: 0 }}>
                None
              </label>
              <div
                style={{
                  alignSelf: "flex-end",
                  marginRight: "10px",
                  marginTop: "15px",
                  padding: "0 10px",
                  opacity: 0,
                }}
                className={REQUESTED_STATUS_CARD}
              >
                <p style={{ marginBottom: 0 }}>No Connection</p>
              </div>
            </>
          )
        )}
      </div>

      <div style={{ margin: "20px 20px" }}>
        {profileFields.map((profileItem) => (
          <div style={{ padding: "5px 10px", marginBottom: "10px" }} key={profileItem.name}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <label className="Body1_400_NTextMidleNavyCustom">{profileItem.name}</label>
              </AccordionSummary>
              <AccordionDetails>
                <div style={{ margin: "16px" }}>
                  {profileItem.value?.map((field) => (
                    <>
                      {field?.FieldValue && (
                        <div
                          className="column-flex"
                          style={{ marginBottom: "16px" }}
                          key={field.FieldName}
                        >
                          <label
                            className={classNameTriskeleParser(
                              field.VisualNameFive,
                              field.VisualTypeFive,
                              "Text"
                            )}
                          >
                            {field.FieldName}
                          </label>
                          <label
                            className={classNameTriskeleParser(
                              isPanelMode ? field.VisualNameSix : field.VisualNameFour,
                              isPanelMode ? field.VisualTypeSix : field.VisualTypeFour,
                              "Text"
                            )}
                          >
                            {field.FieldValue.split("\\n").map((i) => (
                              <>
                                {i}
                                <br />
                              </>
                            ))}
                          </label>
                        </div>
                      )}
                    </>
                  ))}
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProfileView;
